'use client';

import { formatarDoc, validarDoc, docCompleto, type TipoDocumento } from '@/lib/validators';

interface DocInputProps {
  tipo: TipoDocumento;
  value: string;
  onChange: (formatado: string) => void;
  className?: string;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

const PLACEHOLDER: Record<TipoDocumento, string> = {
  cpf: '000.000.000-00',
  cnpj: '00.000.000/0000-00',
};

/**
 * Input de CPF/CNPJ com máscara automática e validação (dígitos verificadores).
 * Exibe erro inline quando o documento está completo mas inválido.
 */
export default function DocInput({
  tipo,
  value,
  onChange,
  className,
  placeholder,
  id,
  disabled,
  'aria-label': ariaLabel,
}: DocInputProps) {
  const invalido = docCompleto(tipo, value) && !validarDoc(tipo, value);
  const label = tipo === 'cpf' ? 'CPF' : 'CNPJ';

  return (
    <div>
      <input
        id={id}
        // CNPJ aceita letras desde a IN RFB 2.229/2024 → teclado alfanumérico.
        // CPF segue exclusivamente numérico.
        inputMode={tipo === 'cpf' ? 'numeric' : 'text'}
        autoCapitalize={tipo === 'cpf' ? undefined : 'characters'}
        spellCheck={false}
        autoComplete="off"
        disabled={disabled}
        aria-label={ariaLabel ?? label}
        aria-invalid={invalido}
        className={`${className ?? ''} ${invalido ? 'border-[var(--error-500)] focus:ring-[var(--error-500)]/30' : ''}`}
        placeholder={placeholder ?? PLACEHOLDER[tipo]}
        value={value}
        maxLength={tipo === 'cpf' ? 14 : 18}
        onChange={(e) => onChange(formatarDoc(tipo, e.target.value))}
      />
      {invalido && (
        <p className="mt-1 text-[11.5px] text-[var(--error-600)]">
          <i className="pi pi-exclamation-circle mr-1 text-[10px]" aria-hidden="true" />
          {label} inválido
        </p>
      )}
    </div>
  );
}
